const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// --- Optimized percentageSeen ---
// Wrapped in requestAnimationFrame + returns promise
function percentageSeen(element) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const rect = element.getBoundingClientRect();
      const elementPositionY = rect.top + scrollY;
      const elementHeight = rect.height;

      if (elementPositionY > scrollY + viewportHeight) {
        resolve(0);
        return;
      } else if (elementPositionY + elementHeight < scrollY) {
        resolve(100);
        return;
      }

      const distance = scrollY + viewportHeight - elementPositionY;
      const percentage = distance / ((viewportHeight + elementHeight) / 100);
      resolve(Math.round(percentage));
    });
  });
}

// Scroll in animation logic
function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const elementTarget = entry.target;

    if (entry.isIntersecting) {
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

// Initialize IntersectionObserver triggers
function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const elements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (elements.length === 0) return;

  if (isDesignModeEvent) {
    elements.forEach((el) => el.classList.add('scroll-trigger--design-mode'));
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  elements.forEach((el) => observer.observe(el));
}

// --- Optimized Zoom-in animation logic ---
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const elements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (elements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  const tickingMap = new WeakMap(); // store per-element scroll throttle

  elements.forEach((element) => {
    let elementIsVisible = false;

    // Use IntersectionObserver to track visibility (avoids forced reflow on scroll)
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    // Initial set
    percentageSeen(element).then((percent) => {
      element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percent);
    });

    // Optimized scroll listener
    window.addEventListener(
      'scroll',
      () => {
        if (!elementIsVisible) return;

        // Throttle using requestAnimationFrame per element
        if (!tickingMap.get(element)) {
          tickingMap.set(element, true);
          requestAnimationFrame(() => {
            percentageSeen(element).then((percent) => {
              element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percent);
            });
            tickingMap.set(element, false);
          });
        }
      },
      { passive: true }
    );
  });
}

// DOMContentLoaded triggers
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

// Shopify design mode events
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
